import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Radar, BookOpen, TrendingUp, Shield, LogOut, Activity, Bot, Link2 } from "lucide-react";
import { LanguageSelector } from "@/components/LanguageSelector";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useNotifications } from "@/hooks/useNotifications";
import { useLiveSession } from "@/hooks/useLiveSession";
import { InstallBanner } from "@/components/InstallBanner";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/copilot", label: "AI Copilot", icon: Radar, testId: "nav-copilot" },
  { to: "/broker", label: "AI Broker", icon: Bot, testId: "nav-broker" },
  { to: "/accounts", label: "Accounts", icon: Link2, testId: "nav-accounts" },
  { to: "/journal", label: "Trade Journal", icon: BookOpen, testId: "nav-journal" },
  { to: "/analytics", label: "Analytics", icon: TrendingUp, testId: "nav-analytics" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useNotifications(!!user);
  const live = useLiveSession();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const access = user?.access;

  return (
    <div className="min-h-screen bg-[#07090E] flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-white/10 bg-[#0D111A] p-5 fixed h-full">
        <Link to="/dashboard" className="flex items-center gap-2 mb-8" data-testid="sidebar-logo">
          <div className="relative">
            <Activity className="w-7 h-7 text-emerald-500" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full pulse-dot" />
          </div>
          <span className="font-display font-extrabold text-lg tracking-tight">Copilot</span>
        </Link>

        {live.active && location.pathname !== "/copilot" && (
          <Link to="/copilot" className="mb-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300 flex items-center gap-2 hover:bg-emerald-500/15 transition-colors" data-testid="live-session-badge">
            <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" /> Live vision session running · return
          </Link>
        )}
        <nav className="flex flex-col gap-1 flex-1">
          {nav.map((n) => {
            const active = location.pathname === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testId}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active ? "bg-emerald-500/15 text-emerald-400" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <n.icon className="w-4 h-4" /> {n.label}
              </Link>
            );
          })}
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === "/admin" ? "bg-cyan-500/15 text-cyan-400" : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}
            >
              <Shield className="w-4 h-4" /> Admin Panel
            </Link>
          )}
        </nav>

        <div className="mb-4" data-testid="sidebar-language">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">AI output language</p>
          <LanguageSelector />
        </div>

        <div className="mb-4" data-testid="sidebar-theme">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Appearance</p>
          <ThemeToggle className="w-full" />
        </div>

        {access && (
          <div className="mb-4 p-3 rounded-lg bg-white/5 border border-white/10">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Access</p>
            {access.reason === "trial" && (
              <p className="text-sm font-semibold text-amber-400" data-testid="access-status">
                Trial · {access.days_left}d left
              </p>
            )}
            {access.reason === "subscription" && (
              <p className="text-sm font-semibold text-emerald-400" data-testid="access-status">Subscribed</p>
            )}
            {access.reason === "admin" && (
              <p className="text-sm font-semibold text-cyan-400" data-testid="access-status">Admin</p>
            )}
            {access.reason === "expired" && (
              <Link to="/dashboard" className="text-sm font-semibold text-red-400" data-testid="access-status">
                Trial ended
              </Link>
            )}
          </div>
        )}

        <div className="flex items-center gap-3 pt-4 border-t border-white/10">
          <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-bold">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
          </div>
          <button onClick={handleLogout} data-testid="logout-button" className="text-slate-500 hover:text-red-400 transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 glass flex items-center justify-between px-4 h-14">
        <Link to="/dashboard" className="flex items-center gap-2">
          <Activity className="w-6 h-6 text-emerald-500" />
          <span className="font-display font-extrabold">Copilot</span>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSelector compact />
          <ThemeToggle compact />
          <button onClick={handleLogout} data-testid="logout-button-mobile" className="text-slate-400">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass flex items-center justify-around h-16">
        {nav.map((n) => {
          const active = location.pathname === n.to;
          return (
            <Link key={n.to} to={n.to} className={`flex flex-col items-center gap-1 ${active ? "text-emerald-400" : "text-slate-500"}`}>
              <n.icon className="w-5 h-5" />
              <span className="text-[10px]">{n.label.split(" ")[0]}</span>
            </Link>
          );
        })}
        {user?.role === "admin" && (
          <Link to="/admin" className={`flex flex-col items-center gap-1 ${location.pathname === "/admin" ? "text-cyan-400" : "text-slate-500"}`}>
            <Shield className="w-5 h-5" /><span className="text-[10px]">Admin</span>
          </Link>
        )}
      </div>

      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 min-w-0">
        <InstallBanner />
        {children}
      </main>
    </div>
  );
}
